import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import AdminEmptyState from './AdminEmptyState'
import AdminPagination from './AdminPagination'
import AdminSkeleton from './AdminSkeleton'
import AdminTransactionDrawer from './AdminTransactionDrawer'
import type { AdminStuckTransaction, AdminTransactionRow } from './adminTypes'
import { exportCsv, formatWhen } from './adminUtils'

type Props = {
  token: string
  initialOpenId?: number | null
  initialFilter?: 'all' | 'stuck'
}

const STATUS_OPTIONS = ['', 'pending', 'accepted', 'shipped', 'delivered', 'completed', 'disputed', 'cancelled']

export default function AdminTransactionsPanel({
  token,
  initialOpenId = null,
  initialFilter = 'all',
}: Props) {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<AdminTransactionRow[]>([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [mode, setMode] = useState<'all' | 'stuck'>(initialFilter)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(initialOpenId)
  const pageSize = 50

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    if (mode === 'stuck') {
      adminAPI
        .stuckTransactions(token, { limit: 100 })
        .then((res) => {
          const stuck = (res.data?.items as AdminStuckTransaction[]) ?? []
          setItems(
            stuck.map((row) => ({
              id: row.transaction_id,
              amount: row.amount ?? 0,
              status: null,
              canonical_state: row.canonical_state,
              buyer_id: row.buyer_id,
              seller_id: row.seller_id,
              buyer_email: null,
              seller_email: null,
              platform_fee: null,
              created_at: row.created_at,
              completed_at: null,
            })),
          )
          setTotal(stuck.length)
        })
        .catch((err) => setError(err.response?.data?.detail || err.message))
        .finally(() => setLoading(false))
      return
    }
    adminAPI
      .listTransactions(token, {
        q: q.trim() || undefined,
        status: status || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      })
      .then((res) => {
        setItems((res.data?.items as AdminTransactionRow[]) ?? [])
        setTotal(Number(res.data?.total ?? 0))
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, q, status, mode, page])

  useEffect(() => {
    const tmr = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(tmr)
  }, [load, q])

  useEffect(() => {
    setMode(initialFilter)
  }, [initialFilter])

  useEffect(() => {
    if (initialOpenId) setSelectedId(initialOpenId)
  }, [initialOpenId])

  useEffect(() => {
    setPage(1)
  }, [q, status, mode])

  function txLabel(state: string) {
    return t(`admin.txState.${state}`, { defaultValue: state })
  }

  function exportRows() {
    exportCsv(
      'transactions.csv',
      ['id', 'amount', 'status', 'buyer', 'seller', 'created_at'],
      items.map((row) => [
        row.id,
        row.amount,
        row.canonical_state,
        row.buyer_email ?? '',
        row.seller_email ?? '',
        row.created_at ?? '',
      ]),
    )
  }

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.transactions')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.transactionsIntro')}</p>
        </div>
        <div className="vc-admin-panel__actions">
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={exportRows}>
            {t('admin.exportCsv')}
          </button>
          <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={load}>
            {loading ? '…' : t('catalog.refresh')}
          </button>
        </div>
      </header>

      <div className="vc-admin-filters">
        <button
          type="button"
          className={`vc-admin-chip${mode === 'all' ? ' is-active' : ''}`}
          onClick={() => setMode('all')}
        >
          {t('admin.txFilter.all')}
        </button>
        <button
          type="button"
          className={`vc-admin-chip${mode === 'stuck' ? ' is-active' : ''}`}
          onClick={() => setMode('stuck')}
        >
          {t('admin.stuckTransactions')}
        </button>
        {mode === 'all'
          ? STATUS_OPTIONS.map((key) => (
              <button
                key={key || 'any-status'}
                type="button"
                className={`vc-admin-chip${status === key ? ' is-active' : ''}`}
                onClick={() => setStatus(key)}
              >
                {key ? t(`admin.txStatus.${key}`, { defaultValue: key }) : t('admin.txFilter.anyStatus')}
              </button>
            ))
          : null}
      </div>

      <input
        type="search"
        className="vc-admin-input vc-admin-input--search"
        placeholder={t('admin.searchTransactions')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        disabled={mode === 'stuck'}
      />

      {error ? <p className="vc-admin-error">{error}</p> : null}
      {loading && items.length === 0 ? <AdminSkeleton variant="table" rows={6} /> : null}
      {!loading && items.length === 0 ? (
        <AdminEmptyState title={t('admin.noTransactions')} hint={t('admin.transactionsIntro')} />
      ) : null}

      {items.length > 0 ? (
        <>
          <div className="vc-admin-table-wrap">
            <table className="vc-admin-table vc-admin-table--clickable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('admin.amount')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('admin.buyer')}</th>
                  <th>{t('admin.seller')}</th>
                  <th>{t('admin.created')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} onClick={() => setSelectedId(row.id)}>
                    <td>#{row.id}</td>
                    <td>€{row.amount.toFixed(2)}</td>
                    <td>{txLabel(row.canonical_state)}</td>
                    <td>{row.buyer_email ?? `#${row.buyer_id}`}</td>
                    <td>{row.seller_email ?? `#${row.seller_id}`}</td>
                    <td>{formatWhen(row.created_at, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {mode === 'all' ? (
            <AdminPagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
          ) : null}
        </>
      ) : null}

      {selectedId ? (
        <AdminTransactionDrawer
          token={token}
          transactionId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={load}
        />
      ) : null}
    </div>
  )
}
